import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFPillToggle from 'src/components/controls/tempRHFControls/RHFPillToggle';

export type ArHistoryReportOrderBy = '1' | '2' | '3';
export type ArHistoryReportBalance = 'A' | 'Y' | 'N';
export type ArHistoryReportFormat = 'PDF' | 'EXCEL';

export interface IArHistoryReportParams {
    format: ArHistoryReportFormat;
    orderBy: ArHistoryReportOrderBy;
    balance: ArHistoryReportBalance;
}

interface IArHistoryReportDialogProps {
    dialogName: string;
    hideDialog: () => void;
    onGenerateReport: (params: IArHistoryReportParams) => void;
    message?: string;
    generating?: boolean;
}

// Order By is shared between the two Oracle popups (Standard block's oid 314, canvas C_STANDARD,
// and the Excel block's oid 344, canvas EXCEL - same three list values in both .fmt poplists).
// Balance Status only exists on the Standard/Report side - XL_AR_History never takes a balance
// param - so it's hidden rather than sent-but-ignored when the toggle is set to Excel.
const ORDER_BY_OPTIONS = [
    { value: '1', label: 'Invoice Date' },
    { value: '2', label: 'Invoice No.' },
    { value: '3', label: 'Prospect No.' },
];

const BALANCE_OPTIONS = [
    { value: 'A', label: 'Show All' },
    { value: 'Y', label: 'Non-Zero Balance' },
    { value: 'N', label: 'Zero Balance' },
];

// One dialog standing in for both Oracle popups (Report's C_STANDARD/W_STANDARD and Excel's
// EXCEL/Excel canvas/window) - a toggle picks the format instead of two separate buttons/dialogs,
// since the two screens only really differ by Balance Status being Report-only.
const ArHistoryReportDialog = (props: IArHistoryReportDialogProps) => {
    const { register, setValue, getValues } = useForm();
    const [isExcel, setIsExcel] = useState<boolean>(false);

    const handleOk = () => {
        const values = getValues();
        props.onGenerateReport({
            format: isExcel ? 'EXCEL' : 'PDF',
            orderBy: (values.orderBy || '1') as ArHistoryReportOrderBy,
            balance: (values.balance || 'A') as ArHistoryReportBalance,
        });
    };

    return (
        <Dialog title={isExcel ? 'A/R History Excel Export' : 'A/R History Report'} dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '30%' }}>
            <form>
                <RHFSelect
                    label="Order By"
                    labelOnLeft={true}
                    labelStyle={{ whiteSpace: 'nowrap' }}
                    name="orderBy"
                    options={ORDER_BY_OPTIONS}
                    defaultVal="1"
                    register={register}
                    style={{ width: '100%' }}
                />
                {!isExcel && (
                    <RHFSelect
                        label="Balance Status"
                        labelOnLeft={true}
                        labelStyle={{ whiteSpace: 'nowrap' }}
                        name="balance"
                        options={BALANCE_OPTIONS}
                        defaultVal="A"
                        register={register}
                        style={{ width: '100%' }}
                    />
                )}
            </form>
            <div className="form-row" style={{ marginTop: '1rem', justifyContent: 'center', alignItems: 'flex-end' }}>
                <RHFPillToggle
                    name="format"
                    items={['PDF', 'Excel']}
                    defaultValue="PDF"
                    register={register}
                    setValue={setValue}
                    onChange={(value) => setIsExcel(value === 'Excel')}
                    style={{ width: '10rem', marginRight: '1rem' }}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '25%', marginRight: '1rem' }}
                    text={props.generating ? 'Generating...' : 'Run Report'}
                    disabled={props.generating}
                    onClick={handleOk}
                />
                <Button className="blue-hover-button" style={{ width: '25%' }} text="Cancel" disabled={props.generating} onClick={() => props.hideDialog()} />
            </div>
            <div style={{ marginTop: '1rem', minHeight: '1.2rem', textAlign: 'center' }}>{props.message}</div>
        </Dialog>
    );
};

export default ArHistoryReportDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/dialogs/ArHistoryReportDialog.tsx